import React, { useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus, faPen, faTrash, faCheck, faTimes } from '@fortawesome/pro-regular-svg-icons'
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import Button from 'src/components/era-package/forms/Button'
import Buttons from 'src/components/era-package/era/Buttons'
import { feeCode as feeCodeOptions } from 'src/views/abi/fields/options'

const blankDraft = () => ({ id: null, hts: '', remark: '', feeList: [] })

const describeFeeCode = (code) => {
  const match = feeCodeOptions.find((option) => option.value === code)
  return match ? `${match.value} - ${match.description}` : code
}

// Client-side fast-feedback only - the create/update service call is the real authority
// for the HTS-uniqueness check (it looks across the whole table, not just this draft).
const validateDraft = (draft) => {
  const errors = {}
  if (!String(draft.hts ?? '').trim()) {
    errors.hts = 'HTS is required'
  }
  return errors
}

export const HtsRuleDialog = ({ open, mode, initialValue, onClose, onSave }) => {
  const [draft, setDraft] = useState(blankDraft)
  const [errors, setErrors] = useState({})

  const [editingFeeIndex, setEditingFeeIndex] = useState(null)
  const [feeDraftCode, setFeeDraftCode] = useState('')
  const [feeError, setFeeError] = useState('')

  useEffect(() => {
    if (open) {
      setDraft(initialValue ? { ...initialValue, feeList: initialValue.feeList ?? [] } : blankDraft())
      setErrors({})
      setEditingFeeIndex(null)
      setFeeDraftCode('')
      setFeeError('')
    }
  }, [open, initialValue])

  const handleFieldChange = (name, value) => {
    setDraft((prev) => ({ ...prev, [name]: value }))
    setErrors((prev) => ({ ...prev, [name]: '' }))
  }

  const startAddFee = () => {
    setEditingFeeIndex(draft.feeList.length)
    setFeeDraftCode('')
    setFeeError('')
  }

  const startEditFee = (index) => {
    setEditingFeeIndex(index)
    setFeeDraftCode(draft.feeList[index].feeCode)
    setFeeError('')
  }

  const cancelFeeEdit = () => {
    setEditingFeeIndex(null)
    setFeeDraftCode('')
    setFeeError('')
  }

  const confirmFeeEdit = () => {
    if (!feeDraftCode) {
      setFeeError('Fee is required')
      return
    }
    const duplicate = draft.feeList.some(
      (fee, index) => fee.feeCode === feeDraftCode && index !== editingFeeIndex
    )
    if (duplicate) {
      setFeeError('This fee code is already in the list')
      return
    }

    setDraft((prev) => {
      const nextFeeList = [...prev.feeList]
      nextFeeList[editingFeeIndex] = { ...nextFeeList[editingFeeIndex], feeCode: feeDraftCode }
      return { ...prev, feeList: nextFeeList }
    })
    cancelFeeEdit()
  }

  const removeFee = (index) => {
    setDraft((prev) => ({ ...prev, feeList: prev.feeList.filter((_, i) => i !== index) }))
    if (editingFeeIndex === index) {
      cancelFeeEdit()
    }
  }

  const handleSave = () => {
    const nextErrors = validateDraft(draft)
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors)
      return
    }
    onSave(draft)
  }

  const isCreate = mode === 'create'

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>HTS Rule</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <TextField
            label="HTS"
            fullWidth
            required
            value={draft.hts}
            error={!!errors.hts}
            helperText={errors.hts}
            onChange={(event) => handleFieldChange('hts', event.target.value)}
          />

          <TextField
            label="Remark"
            fullWidth
            multiline
            minRows={2}
            value={draft.remark}
            onChange={(event) => handleFieldChange('remark', event.target.value)}
          />

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Fee</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Fee</TableCell>
                  <TableCell width={90}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {draft.feeList.map((fee, index) => (
                  <TableRow key={`${fee.feeCode}-${index}`}>
                    <TableCell>
                      {editingFeeIndex === index ? (
                        <TextField
                          select
                          size="small"
                          fullWidth
                          value={feeDraftCode}
                          error={!!feeError}
                          helperText={feeError}
                          onChange={(event) => { setFeeDraftCode(event.target.value); setFeeError('') }}
                        >
                          {feeCodeOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.value} - {option.description}
                            </MenuItem>
                          ))}
                        </TextField>
                      ) : (
                        describeFeeCode(fee.feeCode)
                      )}
                    </TableCell>
                    <TableCell>
                      {editingFeeIndex === index ? (
                        <>
                          <IconButton size="small" onClick={confirmFeeEdit} title="Confirm">
                            <FontAwesomeIcon icon={faCheck} width={14} />
                          </IconButton>
                          <IconButton size="small" onClick={cancelFeeEdit} title="Cancel">
                            <FontAwesomeIcon icon={faTimes} width={14} />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <IconButton size="small" onClick={() => startEditFee(index)} title="Edit">
                            <FontAwesomeIcon icon={faPen} width={14} />
                          </IconButton>
                          <IconButton size="small" onClick={() => removeFee(index)} title="Delete">
                            <FontAwesomeIcon icon={faTrash} width={14} />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {editingFeeIndex === draft.feeList.length && (
                  <TableRow>
                    <TableCell>
                      <TextField
                        select
                        size="small"
                        fullWidth
                        value={feeDraftCode}
                        error={!!feeError}
                        helperText={feeError}
                        onChange={(event) => { setFeeDraftCode(event.target.value); setFeeError('') }}
                      >
                        {feeCodeOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.value} - {option.description}
                          </MenuItem>
                        ))}
                      </TextField>
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={confirmFeeEdit} title="Confirm">
                        <FontAwesomeIcon icon={faCheck} width={14} />
                      </IconButton>
                      <IconButton size="small" onClick={cancelFeeEdit} title="Cancel">
                        <FontAwesomeIcon icon={faTimes} width={14} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <Box display="flex" justifyContent="flex-end" sx={{ mt: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={startAddFee}
                disabled={editingFeeIndex !== null}
                startIcon={<FontAwesomeIcon icon={faPlus} width={10} />}
              >
                Add
              </Button>
            </Box>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ pt: 1 }}>
        <Buttons
          advance={
            <Button variant="contained" onClick={handleSave} title={isCreate ? 'Add this rule' : 'Save changes'}>
              {isCreate ? 'Add' : 'Save'}
            </Button>
          }
          other={
            <Button variant="outlined" onClick={onClose} title="Go back">
              Cancel
            </Button>
          }
        />
      </DialogActions>
    </Dialog>
  )
}

export default HtsRuleDialog
